'use client';

import { useMemo } from 'react';

import {
  useRecentFeaturedListings,
  useShowcaseFeatures,
  useTestimonials,
} from '@/slw/entities/dealer/sell-your-car/sell-your-car.query';
import { ExclusiveOfferings } from '@/slw/widgets/dealer/sell-your-car/exclusive-offerings.ui';
import { DealerApplicationBanner } from '@/slw/widgets/dealer/sell-your-car/application-banner.ui';
import { RegularPackagesSection } from '@/slw/widgets/dealer/sell-your-car/regular-packages-section.ui';
import { ShowcaseSection } from '@/slw/widgets/dealer/sell-your-car/showcase-section.ui';
import { TestimonialsSection } from '@/slw/widgets/dealer/sell-your-car/testimonials-section.ui';
import { Breadcrumb } from '@/slw/widgets/common/breadcrumb.ui';
import { RecentFeaturedListings } from '@/slw/widgets/dealer/sell-your-car/recent-featured-listings.ui';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { PackageOnCard, usePackageOffers, usePackages } from '@/slw/entities/package';
import { convertPackageData } from '@/slw/entities/package/package.util';

export function SellYourCarPage() {
  const { user } = useAuthStore();
  const { data: showcaseFeatures, isLoading: showcaseFeaturesLoading } = useShowcaseFeatures();
  const { data: testimonials, isLoading: testimonialsLoading } = useTestimonials();
  const { data: featuredListings, isLoading: featuredListingsLoading } = useRecentFeaturedListings();

  const { data: packagePlans, isLoading: packagePlansLoading } = usePackages();

  const { data: packageOffers, isLoading: packageOfferLoading } = usePackageOffers();

  // Map packagePlans with PACKAGE_SLOTS to add features
  const enhancedPackagePlans = useMemo<PackageOnCard[]>(() => {
    if (!packagePlans?.data) return [];

    const enhancedPackages = packagePlans.data.map((packagePlan) => {
      return convertPackageData(packagePlan);
    });

    // Then, sort packages to ensure the recommended one is in the middle
    const recommendedPackage = enhancedPackages.find((pkg) => pkg.recommendToTheTop);
    const otherPackages = enhancedPackages.filter((pkg) => !pkg.recommendToTheTop);

    if (recommendedPackage && otherPackages.length <= 2) {
      return enhancedPackages;
    }

    // If we have a recommended package and at least 2 other packages
    if (recommendedPackage && otherPackages.length >= 2) {
      // Split other packages into two groups
      const firstHalf = otherPackages.slice(0, Math.ceil(otherPackages.length / 2));
      const secondHalf = otherPackages.slice(Math.ceil(otherPackages.length / 2));

      // Return with recommended package in the middle
      return [...firstHalf, recommendedPackage, ...secondHalf];
    }

    // If we have a recommended package and only 1 other package
    if (recommendedPackage && otherPackages.length === 1) {
      return [otherPackages[0], recommendedPackage];
    }

    // If we have a recommended package and no other packages
    if (recommendedPackage && otherPackages.length === 0) {
      return [recommendedPackage];
    }

    // If no recommended package, return as is
    return enhancedPackages;
  }, [packagePlans?.data]);

  return (
    <main>
      {/* Title & Subtitle */}
      <div className='container mx-auto px-2 md:px-8'>
        <Breadcrumb data={[{ name: 'Sell Your Car' }]} />
      </div>
      <div className='container mx-auto px-2 md:px-8 mb-8'>
        <h1 className='text-[1.5rem] md:text-[2.5rem] lg:text-[4rem] font-bold mb-2.5'>Buy a Slot for your Listing</h1>
        <p className='text-lg font-semibold'>Show off your classic—pick a package and get noticed!</p>
      </div>

      <div className='container mx-auto px-2 md:px-8 mb-20'>
        <RegularPackagesSection packages={enhancedPackagePlans} isLoading={packagePlansLoading} />
      </div>

      {/* Conditional Dealer Section */}
      {!user.isDealer ? (
        <div className='container mx-auto px-2 md:px-8 mb-20'>
          <DealerApplicationBanner />
        </div>
      ) : (
        <div className='mb-20 bg-primary'>
          <ExclusiveOfferings packageOffers={packageOffers?.data || []} isLoading={packageOfferLoading} />
        </div>
      )}

      {/* Showcase Section */}
      <div
        className='container mx-auto px-8 lg:px-16 2xl:px-0 grid md:grid-cols-2 
      gap-8 my-12 mb-20 divide-x-1 divide-gray-200'
      >
        <ShowcaseSection features={showcaseFeatures?.data || []} isLoading={showcaseFeaturesLoading} />
        <TestimonialsSection testimonials={testimonials?.data || []} isLoading={testimonialsLoading} />
      </div>

      <div className='container mx-auto 2xl:!px-0 xl:px-2.5 mb-20'>
        <RecentFeaturedListings
          dealerStatus={!!user?.isDealer}
          listings={featuredListings?.data || []}
          isLoading={featuredListingsLoading}
        />
      </div>
    </main>
  );
}
